import { formatDate } from '@angular/common';
import { Injectable, LOCALE_ID, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Permission } from 'src/app/common/enums';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { AbsenceDto } from 'src/app/models/dtos/absence-dto';
import { AbsenceStatus } from 'src/app/models/enums/absence-status';
import { AbsenceType } from 'src/app/models/enums/absence-type';
import { AbsenceListRow } from './absence-list-row.model';

@Injectable({ providedIn: 'root' })
export class AbsenceListRowMapper {
  private authService = inject(AuthService);
  private translateService = inject(TranslateService);
  private locale = inject(LOCALE_ID);

  map = (absence: AbsenceDto): AbsenceListRow => {
    const showConfirmAbsence = !absence.ApprovalRequired && absence.StatusId === AbsenceStatus.Draft;
    const showSendToApproval = absence.ApprovalRequired && absence.StatusId === AbsenceStatus.Draft;
    const showDeleteAbsence = absence.StatusId === AbsenceStatus.Draft;
    const showBackToDraftAbsence = absence.StatusId === AbsenceStatus.PendingApproval;
    const showAcceptAbsence = this.isPendingOwnedByDifferentUser(absence);
    const showRejectAbsence = this.isPendingOwnedByDifferentUser(absence);
    const showCancelAbsence = absence.StatusId === AbsenceStatus.Accepted && absence.AbsenceTypeId !== AbsenceType.SickLeave;
    const showApprovingButtons =
      absence.ApprovalPermissions?.some((permissionDto) => this.authService.hasPermission(permissionDto.Permission as Permission)) || false;

    return {
      ...absence,
      showBusinessTime: !absence.SickLeaveId,
      showWorkingTimeTooltip:
        absence.WorkingTimeNominator !== null &&
        !(absence.WorkingTimeNominator === 1 && absence.WorkingTimeDenominator === 1),
      showProgressSpinner:
        absence.StatusId === AbsenceStatus.PendingApproval &&
        absence.TotalApprovalSteps !== null &&
        absence.TotalApprovalSteps > 1,
      showStatusTooltip: absence.StatusId === AbsenceStatus.Accepted || absence.StatusId === AbsenceStatus.Rejected,
      statusTooltip: this.getStatusTooltip(absence),
      statusDatesTooltip: this.getStatusDatesTooltip(absence),
      showConfirmAbsence,
      showSendToApproval,
      showApprovingButtons,
      showAcceptAbsence,
      showRejectAbsence,
      showDeleteAbsence,
      showBackToDraftAbsence,
      showCancelAbsence,
      hasAnyAction:
        !!absence.SickLeaveId ||
        showSendToApproval ||
        showConfirmAbsence ||
        showDeleteAbsence ||
        showBackToDraftAbsence ||
        showAcceptAbsence ||
        showRejectAbsence ||
        showCancelAbsence,
    };
  };

  private isPendingOwnedByDifferentUser(absence: AbsenceDto): boolean {
    return absence.StatusId === AbsenceStatus.PendingApproval && this.authService.authServerUserId !== absence.AuthServerUserId;
  }

  private getStatusDatesTooltip(absence: AbsenceDto): string {
    const formatDate = (date: Date | null) => this.formatDate(date, 'yyyy-MM-dd');

    let tooltip = '';

    if (absence.SentToApprovalOn) {
      tooltip = this.translate('AbsenceTooltip.SubmissionDate', {
        date: formatDate(absence.SentToApprovalOn),
      });
    }

    if (absence.StatusId === AbsenceStatus.Accepted && absence.AcceptedOn) {
      const acceptedText = this.translate('absences.AcceptedOn') + ': ' + formatDate(absence.AcceptedOn);
      tooltip = tooltip ? tooltip + '\n' + acceptedText : acceptedText;
    }

    if (absence.StatusId === AbsenceStatus.Rejected && absence.RejectedOn) {
      const rejectedText = this.translate('absences.RejectedOn') + ': ' + formatDate(absence.RejectedOn);
      tooltip = tooltip ? tooltip + '\n' + rejectedText : rejectedText;
    }

    if (absence.StatusId === AbsenceStatus.PendingApproval && absence.LastDecisiveApprovalDate && absence.LastDecisiveApprovalUserName) {
      const decisionKey = absence.LastDecisiveApprovalIsPositive ? 'AbsenceTooltip.AcceptedBy' : 'AbsenceTooltip.RejectedBy';

      const decisionText = this.translate(decisionKey, {
        userName: absence.LastDecisiveApprovalUserName,
        date: formatDate(absence.LastDecisiveApprovalDate),
      });

      tooltip = tooltip ? tooltip + '\n' + decisionText : decisionText;
    }

    return tooltip;
  }

  private getStatusTooltip(absence: AbsenceDto): string {
    if (absence.IsImported) {
      return this.translate('AbsenceStatusTooltip.Imported');
    }

    if (!absence.ApprovalRequired) {
      return this.translate('AbsenceStatusTooltip.NoApprovalRequired');
    }

    if (absence.StatusId === AbsenceStatus.Accepted) {
      return this.translate('AbsenceStatusTooltip.HistoryInformation', {
        status: absence.Status?.toLowerCase(),
        changedBy: absence.AcceptedBy,
        changedDate: this.formatDate(absence.AcceptedOn, 'yyyy-MM-dd HH:mm'),
      });
    }

    if (absence.StatusId === AbsenceStatus.Rejected) {
      return this.translate('AbsenceStatusTooltip.HistoryInformation', {
        status: absence.Status?.toLowerCase(),
        changedBy: absence.RejectedBy,
        changedDate: this.formatDate(absence.RejectedOn, 'yyyy-MM-dd HH:mm'),
      });
    }

    return '';
  }

  private translate(key: string, params?: Record<string, unknown>): string {
    return this.translateService.instant(key, params);
  }

  private formatDate(date: Date | null, format: string): string {
    return date ? formatDate(date, format, this.locale) : '';
  }
}
